import React, { useState } from 'react';
import { fmtDay, fmtDateTime, periodsOf } from './familyApi';
import { inputCls, PasswordInput } from './CreateAccountForm';

export const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-2xl border border-amber-200 bg-white p-4 space-y-3">
    <h4 className="font-bold text-amber-900">{title}</h4>
    {children}
  </section>
);

export const Btn = ({ tone = 'amber', className = '', ...p }) => {
  const tones = {
    amber: 'bg-amber-500 hover:bg-amber-600 text-white',
    ghost: 'bg-white border-2 border-amber-300 text-amber-900 hover:bg-amber-50',
    red: 'bg-red-600 hover:bg-red-700 text-white',
    green: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  };
  return <button type="button" {...p} className={`rounded-xl px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50 ${tones[tone]} ${className}`} />;
};

export const AccessSummary = ({ a }) => {
  if (a.mode === 'lifetime') return <p data-testid="family-detail-period" className="text-sm text-emerald-800 font-semibold">Lifetime access — every day, no end date.</p>;
  const runs = periodsOf(a.dates);
  return (
    <div data-testid="family-detail-period" className="text-sm space-y-1">
      <p className="text-slate-700"><b>{a.access.access_days_total}</b> access days total · <b>{a.access.access_days_remaining}</b> still to come</p>
      {runs.length === 0 ? <p className="text-slate-500">No access days selected.</p> : (
        <ul className="max-h-32 overflow-y-auto space-y-0.5">
          {runs.map((r) => (
            <li key={r.start} className="text-slate-600">
              {r.start === r.end ? fmtDay(r.start) : `${fmtDay(r.start)} → ${fmtDay(r.end)}`} <span className="text-slate-400">({r.count} day{r.count > 1 ? 's' : ''})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ExtendControls = ({ onExtend, busy }) => {
  const [n, setN] = useState(7);
  const [unit, setUnit] = useState('days');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid="family-extend-amount" type="number" min={1} max={unit === 'days' ? 366 : 24} value={n}
        onChange={(e) => setN(Number(e.target.value))} className={`${inputCls} w-24`} />
      <select data-testid="family-extend-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className={`${inputCls} w-32`}>
        <option value="days">days</option>
        <option value="months">months</option>
      </select>
      <Btn data-testid="family-extend-submit" disabled={busy || n < 1} onClick={() => onExtend(unit === 'days' ? { days: n } : { months: n })}>Extend</Btn>
      <p className="w-full text-xs text-slate-500">Adds consecutive days straight after their last access day (or from today).</p>
    </div>
  );
};

export const PasswordReset = ({ onReset, busy }) => {
  const [pw, setPw] = useState('');
  return (
    <div className="space-y-2">
      <PasswordInput testId="family-reset-password-input" value={pw} onChange={setPw} />
      <Btn data-testid="family-reset-password-submit" disabled={busy || pw.length < 8} onClick={() => onReset(pw).then(() => setPw(''))}>Set new password</Btn>
      <p className="text-xs text-slate-500">They'll be signed out and must use the new password.</p>
    </div>
  );
};

export const History = ({ items }) => (
  <ul data-testid="family-detail-history" className="max-h-40 overflow-y-auto text-xs space-y-1">
    {[...items].reverse().map((h, i) => (
      <li key={i} className="text-slate-600"><span className="text-slate-400">{fmtDateTime(h.at)}</span> — {h.action}</li>
    ))}
  </ul>
);
